/**
 * Pannello admin "Collaborazione Libreria Globale": promuove esercizi e
 * conditioning dalle librerie personali dei coach a quella globale.
 *
 * Il pannello è pensato per lavorare **a lotti**: l'uso reale non è pubblicare
 * un item ogni tanto, è smaltire decine di contenuti non ancora condivisi. Da
 * qui le tre scelte non ovvie:
 *
 * 1. Selezione multipla con range shift-click e "seleziona tutti i
 *    pubblicabili", così 90 item costano 3 interazioni invece di 270.
 * 2. Le azioni bulk sono spezzate in lotti (`BULK_BATCH_SIZE`) anche se il
 *    backend accetterebbe tutto in una richiesta: serve a dare una progress
 *    bar che avanza davvero e a non lasciare la UI cieca per decine di secondi.
 * 3. Dopo ogni lotto lo stato locale viene aggiornato in modo ottimistico e la
 *    lista viene ricaricata **una volta sola** in background. La versione
 *    precedente rifaceva un full reload della dashboard (17 chiamate) dopo ogni
 *    singolo item.
 *
 * Le stats KPI arrivano nella stessa risposta della lista
 * (`GET /admin/collaboration-library`) perché calcolarle richiede comunque la
 * scansione completa di esercizi + conditioning lato server.
 *
 * Spec: ADMIN_COLLABORATION_PHASE1_SPEC.md
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchAPI } from "../../lib/utils";
import { useLanguage } from "../../contexts/LanguageContext";
import { getImageUrl, getYouTubeEmbedUrl } from "../../lib/coachUtils";
import {
	formatEmomItemSummary,
	formatIntervalSecondsLabel,
	getDisplayEmomRounds,
	getEmomRoundLabel,
} from "../../lib/conditioningResultUtils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Progress } from "../ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "../ui/alert-dialog";
import { ScrollArea } from "../ui/scroll-area";
import { Skeleton } from "../ui/skeleton";
import {
	Archive,
	Globe2,
	Loader2,
	RefreshCw,
	Search,
	Share2,
	X,
} from "lucide-react";
import { toast } from "sonner";

const DEFAULT_FILTERS = {
	search: "",
	content_type: "all",
	scope: "all",
	status: "all",
	owner_id: "all",
};

const CONDITIONING_TYPE_LABELS = {
	amrap: "AMRAP",
	for_time: "For Time",
	emom: "EMOM",
	free: "Custom",
};

// Lotti volutamente piccoli: la progress bar avanza ogni ~20 item invece di
// restare ferma fino alla fine.
const BULK_BATCH_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 100;

const itemKey = (item) => `${item.content_type}:${item.item_id}`;

const canPublish = (item) =>
	item.library_scope === "personal" &&
	item.collaboration_status !== "shared_active";

const canArchive = (item) =>
	item.library_scope === "global" && !item.is_archived;

const statusLabel = (status) => {
	if (status === "not_shared") return "Non condiviso";
	if (status === "shared_active") return "Condiviso attivo";
	if (status === "shared_archived") return "Condiviso archiviato";
	if (status === "archived") return "Archiviato";
	if (status === "global_native") return "Globale nativo";
	return status || "-";
};

const statusClass = (status) => {
	if (status === "not_shared") return "bg-zinc-500/20 text-zinc-300";
	if (status === "shared_active") return "bg-emerald-600/20 text-emerald-200";
	if (status === "shared_archived") return "bg-amber-500/20 text-amber-300";
	if (status === "archived") return "bg-rose-500/20 text-rose-300";
	if (status === "global_native") return "bg-indigo-500/20 text-indigo-200";
	return "bg-secondary text-muted-foreground";
};

const scopeLabel = (scope) => (scope === "global" ? "Globale" : "Personale");

const scopeClass = (scope) =>
	scope === "global"
		? "bg-indigo-500/20 text-indigo-200"
		: "bg-emerald-600/20 text-emerald-200";

const contentTypeLabel = (contentType) =>
	contentType === "exercise" ? "Esercizio" : "Conditioning";

const contentTypeClass = (contentType) =>
	contentType === "exercise"
		? "bg-amber-500/20 text-amber-200"
		: "bg-cyan-500/20 text-cyan-200";

const chunk = (list, size) => {
	const batches = [];
	for (let i = 0; i < list.length; i += size) {
		batches.push(list.slice(i, i + size));
	}
	return batches;
};

/**
 * Riga della lista. Memoizzata perché con ~600 item un toggle di selezione
 * altrimenti ri-renderizzerebbe l'intera lista a ogni click.
 */
const CollaborationRow = memo(function CollaborationRow({
	item,
	index,
	selected,
	selectable,
	busy,
	onToggleSelect,
	onOpenDetail,
	onPublish,
	onArchive,
}) {
	const publishable = canPublish(item);
	const archivable = canArchive(item);

	return (
		<div
			className={`p-3 rounded-sm transition-colors ${
				selected ? "bg-volt/10 ring-1 ring-volt/40" : "bg-secondary"
			}`}
		>
			<div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
				<div className="flex items-start gap-3 min-w-0">
					<Checkbox
						checked={selected}
						disabled={!selectable}
						aria-label={`Seleziona ${item.name}`}
						className="mt-1 shrink-0"
						onClick={(event) =>
							onToggleSelect(item, index, event.nativeEvent?.shiftKey)
						}
					/>
					<div className="min-w-0">
						<button
							type="button"
							className="font-medium truncate text-left hover:text-volt transition-colors"
							onClick={() => onOpenDetail(item)}
						>
							{item.name}
						</button>
						<div className="flex flex-wrap items-center gap-2 mt-1">
							<Badge
								className={`border-0 text-[10px] ${scopeClass(item.library_scope)}`}
							>
								{scopeLabel(item.library_scope)}
							</Badge>
							<Badge
								className={`border-0 text-[10px] ${statusClass(item.collaboration_status)}`}
							>
								{statusLabel(item.collaboration_status)}
							</Badge>
							<Badge
								className={`border-0 text-[10px] ${contentTypeClass(item.content_type)}`}
							>
								{contentTypeLabel(item.content_type)}
							</Badge>
						</div>
						<p className="text-xs text-muted-foreground mt-1 truncate">
							Owner: {item.owner_name || "-"} • Tipo: {item.meta_label || "-"}
						</p>
						{item.source_owner_name && (
							<p className="text-xs text-muted-foreground/80 truncate">
								Condiviso da: {item.source_owner_name}
							</p>
						)}
					</div>
				</div>

				<div className="flex flex-wrap items-center gap-2 xl:justify-end shrink-0">
					{busy && (
						<span className="flex items-center gap-1.5 text-xs text-volt">
							<Loader2 className="w-3.5 h-3.5 animate-spin" />
							In corso...
						</span>
					)}
					<Button
						variant="outline"
						size="sm"
						className="rounded-sm"
						onClick={() => onOpenDetail(item)}
					>
						Dettaglio
					</Button>
					{publishable && (
						<Button
							variant="outline"
							size="sm"
							className="rounded-sm border-dashed border-volt/50 text-volt hover:bg-volt/10"
							disabled={busy}
							onClick={() => onPublish(item)}
						>
							Pubblica globale
						</Button>
					)}
					{archivable && (
						<Button
							variant="outline"
							size="sm"
							className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10"
							disabled={busy}
							onClick={() => onArchive(item)}
						>
							Archivia
						</Button>
					)}
				</div>
			</div>
		</div>
	);
});

export function CollaborationLibraryTab({ coaches = [] }) {
	const { language } = useLanguage();

	const [items, setItems] = useState([]);
	const [stats, setStats] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isRefreshing, setIsRefreshing] = useState(false);

	const [filters, setFilters] = useState(DEFAULT_FILTERS);
	const [searchInput, setSearchInput] = useState("");

	const [selectedKeys, setSelectedKeys] = useState(() => new Set());
	const [busyKeys, setBusyKeys] = useState(() => new Set());
	const [bulkProgress, setBulkProgress] = useState(null);
	const [pendingBulk, setPendingBulk] = useState(null);
	const [pendingArchive, setPendingArchive] = useState(null);
	const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

	const [detailItem, setDetailItem] = useState(null);
	const [isDetailOpen, setIsDetailOpen] = useState(false);

	// Le risposte lente di un filtro precedente non devono sovrascrivere
	// quelle del filtro corrente.
	const requestSeq = useRef(0);
	const lastClickedIndex = useRef(null);

	const loadCollaboration = useCallback(
		async ({ background = false } = {}) => {
			const seq = ++requestSeq.current;
			if (background) setIsRefreshing(true);
			else setIsLoading(true);

			try {
				const params = new URLSearchParams();
				params.set("content_type", filters.content_type || "all");
				params.set("scope", filters.scope || "all");
				params.set("status", filters.status || "all");
				if (filters.owner_id && filters.owner_id !== "all") {
					params.set("owner_id", filters.owner_id);
				}
				if (filters.search?.trim()) {
					params.set("search", filters.search.trim());
				}
				const data = await fetchAPI(
					`/admin/collaboration-library?${params.toString()}`,
				);
				if (seq !== requestSeq.current) return;
				setItems(Array.isArray(data?.items) ? data.items : []);
				if (data?.stats) setStats(data.stats);
			} catch (error) {
				if (seq !== requestSeq.current) return;
				toast.error("Errore caricamento collaborazione libreria");
				console.error(error);
			} finally {
				if (seq === requestSeq.current) {
					setIsLoading(false);
					setIsRefreshing(false);
				}
			}
		},
		[filters],
	);

	useEffect(() => {
		loadCollaboration();
	}, [loadCollaboration]);

	// Ricerca con debounce: digitare non deve generare una richiesta per tasto.
	useEffect(() => {
		const timer = setTimeout(() => {
			setFilters((prev) =>
				prev.search === searchInput ? prev : { ...prev, search: searchInput },
			);
		}, SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [searchInput]);

	// Cambiare filtro azzera selezione e paginazione: tenere selezionati item
	// non più visibili renderebbe i contatori della barra azioni bugiardi.
	useEffect(() => {
		setSelectedKeys(new Set());
		setVisibleCount(PAGE_SIZE);
		lastClickedIndex.current = null;
	}, [filters]);

	const setFilter = useCallback((patch) => {
		setFilters((prev) => ({ ...prev, ...patch }));
	}, []);

	const resetFilters = useCallback(() => {
		setSearchInput("");
		setFilters(DEFAULT_FILTERS);
	}, []);

	const ownerOptions = useMemo(() => {
		const map = new Map();
		(coaches || []).forEach((coach) => {
			if (!coach?.user_id) return;
			map.set(coach.user_id, {
				name: coach.name || coach.email || coach.user_id,
				email: coach.email || "",
			});
		});
		(items || []).forEach((item) => {
			if (item?.owner_id && !map.has(item.owner_id)) {
				map.set(item.owner_id, {
					name: item.owner_name || item.owner_id,
					email: item.owner_email || "",
				});
			}
			if (item?.source_owner_id && !map.has(item.source_owner_id)) {
				map.set(item.source_owner_id, {
					name: item.source_owner_name || item.source_owner_id,
					email: item.source_owner_email || "",
				});
			}
		});
		return Array.from(map.entries())
			.map(([value, { name, email }]) => ({ value, name, email }))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [coaches, items]);

	const selectedItems = useMemo(
		() => items.filter((item) => selectedKeys.has(itemKey(item))),
		[items, selectedKeys],
	);
	const selectedPublishable = useMemo(
		() => selectedItems.filter(canPublish),
		[selectedItems],
	);
	const selectedArchivable = useMemo(
		() => selectedItems.filter(canArchive),
		[selectedItems],
	);
	const publishableItems = useMemo(() => items.filter(canPublish), [items]);
	const visibleItems = useMemo(
		() => items.slice(0, visibleCount),
		[items, visibleCount],
	);

	const toggleSelect = useCallback(
		(item, index, withShift) => {
			setSelectedKeys((prev) => {
				const next = new Set(prev);
				const key = itemKey(item);
				const shouldSelect = !next.has(key);

				// Shift-click: estende la selezione dall'ultima riga cliccata,
				// che è il modo naturale di prendere un blocco contiguo.
				if (withShift && lastClickedIndex.current !== null) {
					const [from, to] = [lastClickedIndex.current, index].sort(
						(a, b) => a - b,
					);
					items.slice(from, to + 1).forEach((rangeItem) => {
						if (!canPublish(rangeItem) && !canArchive(rangeItem)) return;
						const rangeKey = itemKey(rangeItem);
						if (shouldSelect) next.add(rangeKey);
						else next.delete(rangeKey);
					});
				} else if (shouldSelect) {
					next.add(key);
				} else {
					next.delete(key);
				}

				lastClickedIndex.current = index;
				return next;
			});
		},
		[items],
	);

	const selectAllActionable = useCallback(() => {
		setSelectedKeys(
			new Set(
				items
					.filter((item) => canPublish(item) || canArchive(item))
					.map(itemKey),
			),
		);
	}, [items]);

	const selectAllPublishable = useCallback(() => {
		setSelectedKeys(new Set(publishableItems.map(itemKey)));
	}, [publishableItems]);

	const clearSelection = useCallback(() => {
		setSelectedKeys(new Set());
		lastClickedIndex.current = null;
	}, []);

	/**
	 * Applica in locale l'esito di una pubblicazione/archiviazione: evita il
	 * reload sincrono e soprattutto evita che la riga "salti" di posizione
	 * mentre si lavora (la lista è ordinata per `updated_at`).
	 */
	const applyOutcomes = useCallback((outcomes, kind) => {
		const touched = new Map(
			outcomes.map((outcome) => [
				`${outcome.content_type}:${outcome.item_id}`,
				outcome.status,
			]),
		);
		setItems((prev) =>
			prev.map((item) => {
				const status = touched.get(itemKey(item));
				if (!status) return item;
				if (kind === "publish" && ["published", "already_published"].includes(status)) {
					return {
						...item,
						collaboration_status: "shared_active",
						shared_in_community: true,
					};
				}
				if (kind === "archive" && ["archived", "already_archived"].includes(status)) {
					return {
						...item,
						is_archived: true,
						collaboration_status: "archived",
					};
				}
				return item;
			}),
		);
	}, []);

	const runBulkAction = useCallback(
		async (targets, kind) => {
			if (!targets.length) return;
			const endpoint =
				kind === "publish"
					? "/admin/collaboration-library/bulk-publish"
					: "/admin/collaboration-library/bulk-archive";
			const batches = chunk(targets, BULK_BATCH_SIZE);

			setBulkProgress({ done: 0, total: targets.length, kind });
			const allResults = [];
			let done = 0;

			try {
				for (const batch of batches) {
					const batchKeys = batch.map(itemKey);
					setBusyKeys(new Set(batchKeys));

					const payload = {
						items: batch.map((item) => ({
							content_type: item.content_type,
							item_id: item.item_id,
						})),
					};
					// eslint-disable-next-line no-await-in-loop -- lotti sequenziali: servono progress reale e carico server prevedibile
					const data = await fetchAPI(endpoint, {
						method: "POST",
						body: JSON.stringify(payload),
					});
					const results = Array.isArray(data?.results) ? data.results : [];
					allResults.push(...results);
					applyOutcomes(results, kind);

					done += batch.length;
					setBulkProgress({ done, total: targets.length, kind });
				}
			} catch (error) {
				toast.error("Operazione interrotta: " + error.message);
				console.error(error);
			} finally {
				setBusyKeys(new Set());
				setBulkProgress(null);
			}

			const okStatus = kind === "publish" ? "published" : "archived";
			const skipStatus =
				kind === "publish" ? "already_published" : "already_archived";
			const ok = allResults.filter((r) => r.status === okStatus).length;
			const skipped = allResults.filter((r) => r.status === skipStatus).length;
			const failed = allResults.length - ok - skipped;

			const verb = kind === "publish" ? "pubblicati" : "archiviati";
			if (ok > 0) {
				const extra = [
					skipped > 0 ? `${skipped} già presenti` : null,
					failed > 0 ? `${failed} falliti` : null,
				].filter(Boolean);
				toast.success(
					`${ok} contenuti ${verb}${extra.length ? ` · ${extra.join(" · ")}` : ""}`,
				);
			} else if (failed > 0) {
				toast.error(`Nessun contenuto ${verb}: ${failed} falliti`);
			} else if (skipped > 0) {
				toast.info(`Tutti i contenuti selezionati erano già ${verb}`);
			}

			clearSelection();
			loadCollaboration({ background: true });
		},
		[applyOutcomes, clearSelection, loadCollaboration],
	);

	const publishSingle = useCallback(
		async (item) => {
			const key = itemKey(item);
			setBusyKeys((prev) => new Set(prev).add(key));
			try {
				await fetchAPI("/admin/collaboration-library/publish", {
					method: "POST",
					body: JSON.stringify({
						content_type: item.content_type,
						source_id: item.item_id,
					}),
				});
				applyOutcomes(
					[
						{
							content_type: item.content_type,
							item_id: item.item_id,
							status: "published",
						},
					],
					"publish",
				);
				toast.success(`"${item.name}" pubblicato nella libreria globale`);
				loadCollaboration({ background: true });
			} catch (error) {
				toast.error("Errore: " + error.message);
			} finally {
				setBusyKeys((prev) => {
					const next = new Set(prev);
					next.delete(key);
					return next;
				});
			}
		},
		[applyOutcomes, loadCollaboration],
	);

	const archiveSingle = useCallback(
		async (item) => {
			const key = itemKey(item);
			setBusyKeys((prev) => new Set(prev).add(key));
			try {
				await fetchAPI("/admin/collaboration-library/archive", {
					method: "POST",
					body: JSON.stringify({
						content_type: item.content_type,
						global_id: item.item_id,
					}),
				});
				applyOutcomes(
					[
						{
							content_type: item.content_type,
							item_id: item.item_id,
							status: "archived",
						},
					],
					"archive",
				);
				toast.success(`"${item.name}" archiviato dalla libreria globale`);
				loadCollaboration({ background: true });
			} catch (error) {
				toast.error("Errore: " + error.message);
			} finally {
				setBusyKeys((prev) => {
					const next = new Set(prev);
					next.delete(key);
					return next;
				});
			}
		},
		[applyOutcomes, loadCollaboration],
	);

	const openDetail = useCallback((item) => {
		setDetailItem(item);
		setIsDetailOpen(true);
	}, []);

	// Callback stabile: una arrow inline nel map vanificherebbe il memo delle righe.
	const requestArchive = useCallback((item) => setPendingArchive(item), []);

	const detailPreview = detailItem?.preview || null;
	const detailVideoUrl =
		detailItem?.content_type === "exercise"
			? getYouTubeEmbedUrl(detailPreview?.video_url)
			: "";
	const detailImageUrl =
		detailItem?.content_type === "exercise"
			? getImageUrl(detailPreview?.image_url)
			: "";
	const detailEmomRounds = useMemo(() => {
		if (detailItem?.content_type !== "conditioning") return [];
		return getDisplayEmomRounds(detailPreview || {});
	}, [detailItem, detailPreview]);
	const detailConditioningRows = Array.isArray(
		detailPreview?.conditioning_exercises,
	)
		? detailPreview.conditioning_exercises
		: [];

	const isBulkRunning = !!bulkProgress;
	const kpiCards = [
		{
			label: "Personali Totali",
			value: stats?.personal_total || 0,
			filter: { scope: "personal", status: "all" },
		},
		{
			label: "Globali Attivi",
			value: stats?.global_total || 0,
			filter: { scope: "global", status: "all" },
		},
		{
			label: "Non Condivisi",
			value: stats?.not_shared_total || 0,
			filter: { scope: "all", status: "not_shared" },
		},
		{
			label: "Condivisi Community",
			value: stats?.community_shared_total || 0,
			filter: { scope: "global", status: "shared_active" },
		},
		{
			label: "Archiviati",
			value: stats?.archived_total || 0,
			filter: { scope: "all", status: "archived" },
		},
	];

	return (
		<div className="space-y-4">
			<Card className="bg-card border-border rounded-sm">
				<CardHeader>
					<CardTitle className="font-heading text-xl flex items-center gap-2">
						<Share2 className="w-5 h-5 text-volt" />
						Collaborazione Libreria Globale
						{isRefreshing && (
							<RefreshCw className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
						)}
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
						<div className="xl:col-span-2 relative">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
							<Input
								placeholder="Cerca nome, owner, tipo..."
								value={searchInput}
								onChange={(e) => setSearchInput(e.target.value)}
								className="pl-10 pr-9 bg-secondary border-transparent rounded-sm"
							/>
							{searchInput && (
								<button
									type="button"
									aria-label="Pulisci ricerca"
									className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
									onClick={() => setSearchInput("")}
								>
									<X className="w-4 h-4" />
								</button>
							)}
						</div>
						<Select
							value={filters.content_type}
							onValueChange={(value) => setFilter({ content_type: value })}
						>
							<SelectTrigger className="bg-secondary border-transparent rounded-sm">
								<SelectValue placeholder="Tipo" />
							</SelectTrigger>
							<SelectContent className="bg-card border-border">
								<SelectItem value="all">Tutti i tipi</SelectItem>
								<SelectItem value="exercise">Esercizi</SelectItem>
								<SelectItem value="conditioning">Conditioning</SelectItem>
							</SelectContent>
						</Select>
						<Select
							value={filters.scope}
							onValueChange={(value) => setFilter({ scope: value })}
						>
							<SelectTrigger className="bg-secondary border-transparent rounded-sm">
								<SelectValue placeholder="Scope" />
							</SelectTrigger>
							<SelectContent className="bg-card border-border">
								<SelectItem value="all">Tutti gli scope</SelectItem>
								<SelectItem value="personal">Personali</SelectItem>
								<SelectItem value="global">Globali</SelectItem>
							</SelectContent>
						</Select>
						<Select
							value={filters.status}
							onValueChange={(value) => setFilter({ status: value })}
						>
							<SelectTrigger className="bg-secondary border-transparent rounded-sm">
								<SelectValue placeholder="Stato" />
							</SelectTrigger>
							<SelectContent className="bg-card border-border">
								<SelectItem value="all">Tutti gli stati</SelectItem>
								<SelectItem value="not_shared">Non condivisi</SelectItem>
								<SelectItem value="shared_active">Condivisi attivi</SelectItem>
								<SelectItem value="archived">Archiviati</SelectItem>
								<SelectItem value="global_native">Globali nativi</SelectItem>
							</SelectContent>
						</Select>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 mt-3">
						<Select
							value={filters.owner_id}
							onValueChange={(value) => setFilter({ owner_id: value })}
						>
							<SelectTrigger className="bg-secondary border-transparent rounded-sm">
								<SelectValue placeholder="Owner">
									{filters.owner_id === "all"
										? "Tutti i coach"
										: ownerOptions.find(
												(owner) => owner.value === filters.owner_id,
											)?.name}
								</SelectValue>
							</SelectTrigger>
							<SelectContent className="bg-card border-border">
								<SelectItem value="all">Tutti i coach</SelectItem>
								{ownerOptions.map((owner) => (
									<SelectItem key={owner.value} value={owner.value}>
										<span className="flex min-w-0 flex-col text-left">
											<span>{owner.name}</span>
											{owner.email && (
												<span className="truncate text-xs text-muted-foreground">
													{owner.email}
												</span>
											)}
										</span>
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<div className="xl:col-span-4 flex items-center justify-end gap-2">
							<Button
								variant="outline"
								className="rounded-sm"
								onClick={() => loadCollaboration({ background: true })}
								disabled={isLoading || isBulkRunning}
							>
								<RefreshCw
									className={`w-4 h-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`}
								/>
								Aggiorna
							</Button>
							<Button
								variant="outline"
								className="rounded-sm"
								onClick={resetFilters}
							>
								Reset filtri
							</Button>
						</div>
					</div>
				</CardContent>
			</Card>

			<div className="grid grid-cols-2 xl:grid-cols-5 gap-4">
				{kpiCards.map((card) => {
					const isActive =
						filters.scope === card.filter.scope &&
						filters.status === card.filter.status;
					return (
						<button
							key={card.label}
							type="button"
							onClick={() => setFilter(card.filter)}
							className="text-left"
						>
							<Card
								className={`bg-card border-border rounded-sm transition-colors hover:border-volt/50 ${
									isActive ? "border-volt/70 ring-1 ring-volt/30" : ""
								}`}
							>
								<CardContent className="p-4">
									<p className="text-xs text-muted-foreground uppercase tracking-wider">
										{card.label}
									</p>
									<p className="text-2xl font-heading">{card.value}</p>
								</CardContent>
							</Card>
						</button>
					);
				})}
			</div>

			<Card className="bg-card border-border rounded-sm">
				<CardHeader className="pb-3">
					<div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
						<CardTitle className="font-heading text-xl">
							Gestione Collaborazione Contenuti
							<span className="ml-2 text-sm font-normal text-muted-foreground">
								{isLoading ? "" : `${items.length} risultati`}
							</span>
						</CardTitle>
						<div className="flex flex-wrap items-center gap-2">
							<Button
								variant="outline"
								size="sm"
								className="rounded-sm"
								disabled={!publishableItems.length || isBulkRunning}
								onClick={selectAllPublishable}
							>
								Seleziona pubblicabili ({publishableItems.length})
							</Button>
							<Button
								variant="outline"
								size="sm"
								className="rounded-sm"
								disabled={!items.length || isBulkRunning}
								onClick={selectAllActionable}
							>
								Seleziona tutti
							</Button>
							{selectedKeys.size > 0 && (
								<Button
									variant="ghost"
									size="sm"
									className="rounded-sm"
									disabled={isBulkRunning}
									onClick={clearSelection}
								>
									<X className="w-4 h-4 mr-1" />
									Deseleziona
								</Button>
							)}
						</div>
					</div>
				</CardHeader>

				{(selectedKeys.size > 0 || isBulkRunning) && (
					<div className="mx-6 mb-3 rounded-sm border border-volt/40 bg-volt/5 p-3">
						{isBulkRunning ? (
							<div className="space-y-2">
								<div className="flex items-center justify-between text-sm">
									<span className="flex items-center gap-2 text-volt">
										<Loader2 className="w-4 h-4 animate-spin" />
										{bulkProgress.kind === "publish"
											? "Pubblicazione in corso"
											: "Archiviazione in corso"}
									</span>
									<span className="text-muted-foreground tabular-nums">
										{bulkProgress.done} / {bulkProgress.total}
									</span>
								</div>
								<Progress
									value={Math.round(
										(bulkProgress.done / Math.max(bulkProgress.total, 1)) * 100,
									)}
									className="h-1.5"
								/>
							</div>
						) : (
							<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
								<p className="text-sm">
									<span className="font-medium text-volt">
										{selectedKeys.size} selezionati
									</span>
									<span className="text-muted-foreground">
										{" "}
										· {selectedPublishable.length} pubblicabili ·{" "}
										{selectedArchivable.length} archiviabili
									</span>
								</p>
								<div className="flex flex-wrap items-center gap-2">
									<Button
										size="sm"
										className="rounded-sm bg-volt text-black hover:bg-volt/90"
										disabled={!selectedPublishable.length}
										onClick={() =>
											setPendingBulk({
												kind: "publish",
												targets: selectedPublishable,
											})
										}
									>
										<Globe2 className="w-4 h-4 mr-2" />
										Pubblica {selectedPublishable.length}
									</Button>
									<Button
										variant="outline"
										size="sm"
										className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10"
										disabled={!selectedArchivable.length}
										onClick={() =>
											setPendingBulk({
												kind: "archive",
												targets: selectedArchivable,
											})
										}
									>
										<Archive className="w-4 h-4 mr-2" />
										Archivia {selectedArchivable.length}
									</Button>
								</div>
							</div>
						)}
					</div>
				)}

				<CardContent>
					<ScrollArea className="h-[560px]">
						<div className="space-y-2 pr-3">
							{isLoading ? (
								Array.from({ length: 6 }).map((_, index) => (
									<Skeleton
										key={index}
										className="h-[92px] w-full rounded-sm bg-secondary"
									/>
								))
							) : items.length === 0 ? (
								<div className="p-6 text-sm text-muted-foreground">
									Nessun contenuto trovato con i filtri correnti.
								</div>
							) : (
								<>
									{visibleItems.map((item, index) => {
										const key = itemKey(item);
										return (
											<CollaborationRow
												key={key}
												item={item}
												index={index}
												selected={selectedKeys.has(key)}
												selectable={canPublish(item) || canArchive(item)}
												busy={busyKeys.has(key)}
												onToggleSelect={toggleSelect}
												onOpenDetail={openDetail}
												onPublish={publishSingle}
												onArchive={requestArchive}
											/>
										);
									})}
									{visibleCount < items.length && (
										<Button
											variant="outline"
											className="w-full rounded-sm"
											onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
										>
											Mostra altri ({items.length - visibleCount} rimanenti)
										</Button>
									)}
								</>
							)}
						</div>
					</ScrollArea>
				</CardContent>
			</Card>

			<AlertDialog
				open={!!pendingBulk}
				onOpenChange={(open) => !open && setPendingBulk(null)}
			>
				<AlertDialogContent className="bg-card border-border rounded-sm">
					<AlertDialogHeader>
						<AlertDialogTitle className="font-heading">
							{pendingBulk?.kind === "publish"
								? `Pubblicare ${pendingBulk?.targets.length} contenuti?`
								: `Archiviare ${pendingBulk?.targets.length} contenuti?`}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{pendingBulk?.kind === "publish"
								? "I contenuti selezionati verranno copiati nella libreria globale e saranno visibili a tutti i coach. L'operazione è reversibile tramite archiviazione."
								: "I contenuti globali selezionati verranno archiviati: non saranno più disponibili ai coach, ma lo storico resta consultabile."}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
						<AlertDialogAction
							className="rounded-sm"
							onClick={() => {
								const payload = pendingBulk;
								setPendingBulk(null);
								if (payload) runBulkAction(payload.targets, payload.kind);
							}}
						>
							Conferma
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog
				open={!!pendingArchive}
				onOpenChange={(open) => !open && setPendingArchive(null)}
			>
				<AlertDialogContent className="bg-card border-border rounded-sm">
					<AlertDialogHeader>
						<AlertDialogTitle className="font-heading">
							Archiviare "{pendingArchive?.name}"?
						</AlertDialogTitle>
						<AlertDialogDescription>
							Il contenuto non sarà più disponibile nella libreria globale. Lo
							storico resta consultabile.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
						<AlertDialogAction
							className="rounded-sm"
							onClick={() => {
								const target = pendingArchive;
								setPendingArchive(null);
								if (target) archiveSingle(target);
							}}
						>
							Archivia
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
				<DialogContent className="bg-card border-border max-w-2xl rounded-sm max-h-[85vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle className="font-heading text-xl">
							Dettaglio Collaborazione
						</DialogTitle>
						<DialogDescription>{detailItem?.name || "-"}</DialogDescription>
					</DialogHeader>
					{detailItem && (
						<div className="space-y-3 text-sm">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
								<div className="p-3 bg-secondary rounded-sm">
									<p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
										Tipo contenuto
									</p>
									<div>
										<Badge
											className={`border-0 ${contentTypeClass(detailItem.content_type)}`}
										>
											{contentTypeLabel(detailItem.content_type)}
										</Badge>
									</div>
								</div>
								<div className="p-3 bg-secondary rounded-sm">
									<p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
										Scope
									</p>
									<p className="font-medium">
										{scopeLabel(detailItem.library_scope)}
									</p>
								</div>
								<div className="p-3 bg-secondary rounded-sm">
									<p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
										Owner attuale
									</p>
									<p className="font-medium">{detailItem.owner_name || "-"}</p>
								</div>
								<div className="p-3 bg-secondary rounded-sm">
									<p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
										Provenienza
									</p>
									<p className="font-medium">
										{detailItem.source_owner_name || "-"}
									</p>
								</div>
							</div>

							<div className="p-3 bg-secondary rounded-sm">
								<p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
									Stato collaborazione
								</p>
								<p className="font-medium">
									{statusLabel(detailItem.collaboration_status)}
								</p>
							</div>

							<div className="p-4 bg-secondary rounded-sm space-y-4">
								<div className="flex items-center justify-between gap-3">
									<p className="text-xs text-muted-foreground uppercase tracking-wider">
										Anteprima contenuto
									</p>
									<p className="text-xs text-muted-foreground">
										{detailItem.meta_label || "-"}
									</p>
								</div>

								{detailItem.content_type === "exercise" ? (
									<div className="space-y-4">
										{detailVideoUrl ? (
											<div className="space-y-2">
												<p className="text-xs text-muted-foreground uppercase tracking-wider">
													Video completo
												</p>
												<div className="aspect-video rounded-sm overflow-hidden border border-border bg-black">
													<iframe
														src={detailVideoUrl}
														title={detailItem.name}
														className="w-full h-full"
														allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
														allowFullScreen
													/>
												</div>
											</div>
										) : detailImageUrl ? (
											<div className="space-y-2">
												<p className="text-xs text-muted-foreground uppercase tracking-wider">
													Immagine
												</p>
												<img
													src={detailImageUrl}
													alt={detailItem.name}
													className="w-full max-h-[320px] object-cover rounded-sm border border-border"
												/>
											</div>
										) : null}

										{detailPreview?.description && (
											<div className="space-y-2">
												<p className="text-xs text-muted-foreground uppercase tracking-wider">
													Descrizione
												</p>
												<p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">
													{detailPreview.description}
												</p>
											</div>
										)}

										{(detailPreview?.tags || []).length > 0 && (
											<div className="space-y-2">
												<p className="text-xs text-muted-foreground uppercase tracking-wider">
													Tag
												</p>
												<div className="flex flex-wrap gap-2">
													{detailPreview.tags.map((tag) => (
														<Badge
															key={tag}
															className="border-0 bg-zinc-500/20 text-zinc-200"
														>
															{tag}
														</Badge>
													))}
												</div>
											</div>
										)}
									</div>
								) : (
									<div className="space-y-4">
										<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
											<div className="rounded-sm border border-border bg-card/40 p-3">
												<p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
													Tipo
												</p>
												<p className="font-medium">
													{CONDITIONING_TYPE_LABELS[
														detailPreview?.conditioning_type
													] ||
														detailPreview?.conditioning_type ||
														"Custom"}
												</p>
											</div>
											<div className="rounded-sm border border-border bg-card/40 p-3">
												<p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
													Struttura
												</p>
												<p className="font-medium">
													{detailPreview?.conditioning_type === "emom" &&
													detailPreview?.interval_seconds
														? `Ogni ${formatIntervalSecondsLabel(detailPreview.interval_seconds, language)}`
														: detailPreview?.rounds ||
															detailPreview?.hiit_cycles ||
															"-"}
												</p>
											</div>
										</div>

										{detailPreview?.description && (
											<p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">
												{detailPreview.description}
											</p>
										)}

										{detailPreview?.conditioning_free_text && (
											<div className="space-y-2">
												<p className="text-xs text-muted-foreground uppercase tracking-wider">
													Schema libero
												</p>
												<pre className="whitespace-pre-wrap rounded-sm border border-border bg-card/40 p-3 text-xs leading-relaxed text-foreground/90 font-mono">
													{detailPreview.conditioning_free_text}
												</pre>
											</div>
										)}

										{detailEmomRounds.length > 0 && (
											<div className="space-y-3">
												<p className="text-xs text-muted-foreground uppercase tracking-wider">
													Round EMOM configurati
												</p>
												<div className="space-y-2">
													{detailEmomRounds.map((round, index) => (
														<div
															key={round.round_id || `${index}`}
															className="rounded-sm border border-border bg-card/40 p-3"
														>
															<p className="font-medium text-sm mb-2">
																{getEmomRoundLabel(round, index, language)}
															</p>
															<div className="space-y-1 text-xs text-foreground/90">
																{(round.items || []).map((item, itemIndex) => (
																	<p
																		key={item.item_id || `${index}-${itemIndex}`}
																	>
																		{formatEmomItemSummary(item, language)}
																	</p>
																))}
															</div>
														</div>
													))}
												</div>
											</div>
										)}

										{detailConditioningRows.length > 0 && (
											<div className="space-y-3">
												<p className="text-xs text-muted-foreground uppercase tracking-wider">
													Esercizi configurati
												</p>
												<div className="space-y-2">
													{detailConditioningRows.map((row, index) => (
														<div
															key={row.exercise_id || `${index}`}
															className="rounded-sm border border-border bg-card/40 p-3"
														>
															<div className="flex items-start justify-between gap-3">
																<div>
																	<p className="font-medium text-sm">
																		{row.exercise_name ||
																			row.custom_text ||
																			row.description ||
																			`Step ${index + 1}`}
																	</p>
																	{row.notes && (
																		<p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">
																			{row.notes}
																		</p>
																	)}
																</div>
																<div className="text-right text-xs text-muted-foreground space-y-1 shrink-0">
																	{row.reps && <p>{row.reps} reps</p>}
																	{row.load && <p>{row.load}</p>}
																	{row.rest && <p>Rest {row.rest}</p>}
																</div>
															</div>
														</div>
													))}
												</div>
											</div>
										)}
									</div>
								)}
							</div>

							<div className="flex items-center justify-end gap-2 pt-1">
								{canPublish(detailItem) && (
									<Button
										variant="outline"
										className="rounded-sm border-dashed border-volt/50 text-volt hover:bg-volt/10"
										disabled={busyKeys.has(itemKey(detailItem))}
										onClick={() => {
											setIsDetailOpen(false);
											publishSingle(detailItem);
										}}
									>
										Pubblica globale
									</Button>
								)}
								{canArchive(detailItem) && (
									<Button
										variant="outline"
										className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10"
										disabled={busyKeys.has(itemKey(detailItem))}
										onClick={() => {
											setIsDetailOpen(false);
											setPendingArchive(detailItem);
										}}
									>
										Archivia globale
									</Button>
								)}
							</div>
						</div>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}
